import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { SharedModule } from '@app/shared/shared.module';
import { base64ToBlob } from '@app/shared/utils/file.util';

/**
 * Chromeless document viewer built on ngx-extended-pdf-viewer, with its
 * built-in pinch-to-zoom (no zoom buttons, no toolbar). Used where the
 * document should fill the screen and be zoomed with fingers on mobile.
 *
 * Kept separate from `c-file-view` (which stays on ng2-pdf-viewer + buttons
 * for the other document screens).
 */
@Component({
  selector: 'c-pdf-view',
  templateUrl: './pdf-view.component.html',
  styleUrl: './pdf-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, NgxExtendedPdfViewerModule],
})
export class PdfViewComponent {
  isImage = false;
  imageSrc: string | undefined;
  pdfSrc: Blob | Uint8Array | undefined;

  @Input({ required: true })
  set file(value: string | Uint8Array) {
    this.isImage = this.isFileAnImage(value);
    if (this.isImage) {
      this.imageSrc = typeof value === 'string' ? value : undefined;
      this.pdfSrc = undefined;
    } else {
      this.imageSrc = undefined;
      // ngx-extended-pdf-viewer accepts a Blob directly; converting the
      // base64 data URI is more reliable than passing a huge data-URI string.
      this.pdfSrc = typeof value === 'string' ? base64ToBlob(value) : value;
    }
  }

  private isFileAnImage(file: string | Uint8Array): boolean {
    if (typeof file !== 'string') {
      return false;
    }
    const match = file.match(/data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+).*,.*/);
    return !!match && match[1].startsWith('image/');
  }
}
